/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { searchUrlBuilder } from '@/lib/url';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';

interface RecentSearchesProps {
    recentSearches?: string[];
    closeAndNavigate: (link: string) => void;
    clearRecentSearches: () => void;
}

function RecentSearchButton({
    recentSearch,
    closeAndNavigate,
}: {
    recentSearch: string;
    closeAndNavigate: (link: string) => void;
}) {
    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            className="!rounded-full text-xs shrink-0 border-0 bg-brd-50 text-foreground hover:bg-brd-50/90 font-normal"
            data-slot="suggestion"
            onClick={() => {
                closeAndNavigate(searchUrlBuilder(recentSearch));
            }}>
            {recentSearch}
        </Button>
    );
}

export default function RecentSearches({
    recentSearches = [],
    closeAndNavigate,
    clearRecentSearches,
}: RecentSearchesProps) {
    const { t } = useTranslation('search');

    if (recentSearches.length === 0) {
        return null;
    }

    return (
        <div className="flex items-center gap-3 min-w-0 w-full">
            <div className="text-sm font-semibold text-foreground shrink-0">{t('suggestions.recentSearches')}</div>
            <Carousel opts={{ align: 'start', dragFree: true, containScroll: 'trimSnaps' }} className="min-w-0 flex-1">
                <CarouselContent className="ml-0 gap-2">
                    {recentSearches.map((recentSearch) => (
                        <CarouselItem key={recentSearch} className="basis-auto pl-0">
                            <RecentSearchButton recentSearch={recentSearch} closeAndNavigate={closeAndNavigate} />
                        </CarouselItem>
                    ))}
                    <CarouselItem className="basis-auto pl-0">
                        <Button
                            type="button"
                            aria-label={t('suggestions.clearRecentSearches')}
                            variant="link"
                            size="sm"
                            onClick={clearRecentSearches}
                            className="text-xs underline font-normal text-foreground hover:text-foreground/60 hover:bg-transparent">
                            {t('suggestions.clearAll')}
                        </Button>
                    </CarouselItem>
                </CarouselContent>
            </Carousel>
        </div>
    );
}
